import { Component, effect, inject } from "@angular/core";
import { NgGlyph } from "@ng-icons/core";
import { Router } from "@angular/router";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { AppAvatarComponent } from "../../../../Common/Components/Avatar/AvatarComponent";
import { AuthState } from "../../../Auth/State/AuthState";
import { AuthEffects } from "../../../Auth/State/AuthEffects";
import { UserListItem } from "../../Dto/UserModel";
import { FileApi } from "../../../File/Api/FileApi";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { ProfileEffects } from "../../State/ProfileEffect";
import { ProfileState } from "../../State/ProfileState";
import { PermissionGroup, ProfilePermission } from "../../Dto/ProfilePermissionModel";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-user-profile",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, AppCardComponent, AppAvatarComponent, NgGlyph],
  templateUrl: "./UserProfilePage.html",
  styleUrl: "./UserProfilePage.scss",
})
export class UserProfilePage {
  readonly authState = inject(AuthState);
  private readonly authEffects = inject(AuthEffects);
  private readonly fileApi = inject(FileApi);
  private readonly toastEffects = inject(ToastEffects);
  private readonly profileEffects = inject(ProfileEffects);
  readonly profileState = inject(ProfileState);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  readonly permissionSearch = new FormControl("", { nonNullable: true });

  profileForm: FormGroup;
  passwordForm: FormGroup;
  loading = this.profileState.loading;
  error = this.profileState.error;
  isPasswordSaving = false;

  constructor() {
    this.profileForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      email: ["", [Validators.required, Validators.email]],
      phone: [""],
      address: [""],
      imageUrl: [""],
    });
    this.passwordForm = this.fb.group(
      {
        currentPassword: ["", [Validators.required, Validators.minLength(8)]],
        newPassword: ["", [Validators.required, Validators.minLength(8)]],
        confirmPassword: ["", [Validators.required, Validators.minLength(8)]],
      },
      { validators: passwordMatchValidator },
    );
    effect(() => {
      const currentUser = this.authState.user();
      if (!currentUser) {
        return;
      }

      const currentProfile = this.profileState.profile();
      if (!currentProfile || currentProfile.id !== currentUser.id) {
        this.profileEffects.fetchProfile(currentUser.id).catch(() => undefined);
      }
    });

    effect(() => {
      const profile = this.profileState.profile();
      if (!profile || this.profileForm.dirty) {
        return;
      }

      this.profileForm.patchValue(
        {
          name: profile.name,
          email: profile.email,
          phone: profile.phone ?? "",
          address: profile.address ?? "",
          imageUrl: profile.imageUrl ?? "",
        },
        { emitEvent: false },
      );
    });
  }

  async onSubmit(): Promise<void> {
    if (this.profileForm.invalid) {
      return;
    }

    const formData = this.profileForm.value;

    const currentUser = this.authState.user();
    if (!currentUser) {
      console.error("No authenticated user found");
      return;
    }

    // Only include fields that are defined and not empty
    const profileData: Partial<UserListItem> = {};
    if (formData.name) profileData.name = formData.name;
    if (formData.email) profileData.email = formData.email;
    if (formData.phone !== undefined) profileData.phone = formData.phone;
    if (formData.address !== undefined) profileData.address = formData.address;

    try {
      await this.profileEffects.updateProfile(currentUser.id, profileData);
      this.toastEffects.show("Profile updated successfully", "success");
    } catch (error: unknown) {
      this.toastEffects.show(getBackendErrorMessage(error, "Failed to update profile"), "error");
    }
  }

  async onChangePassword(): Promise<void> {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const { currentPassword, newPassword } = this.passwordForm.value;
    if (!currentPassword || !newPassword) {
      return;
    }

    this.isPasswordSaving = true;
    try {
      const result = await this.authEffects.resetPassword({ currentPassword, newPassword });
      if (result.success) {
        this.toastEffects.show(result.message, "success");
        this.passwordForm.reset();
      } else {
        this.toastEffects.show(result.message, "error");
      }
    } catch (error: unknown) {
      this.toastEffects.show(getBackendErrorMessage(error, "Failed to update password"), "error");
    } finally {
      this.isPasswordSaving = false;
    }
  }

  get permissionGroups(): PermissionGroup[] {
    const permissions = this.getProfilePermissions();
    const term = this.permissionSearch.value.trim().toLowerCase();
    const grouped = new Map<string, ProfilePermission[]>();

    for (const permission of permissions) {
      if (term && !this.matchesSearch(permission, term)) {
        continue;
      }

      const existing = grouped.get(permission.module) ?? [];
      existing.push(permission);
      grouped.set(permission.module, existing);
    }

    return Array.from(grouped.entries()).map(([module, permissions]) => ({
      module,
      permissions,
    }));
  }

  onCancel(): void {
    this.router.navigate(["/home"]);
  }

  async onAvatarChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const currentUser = this.authState.user();
    if (!currentUser) {
      console.error("No authenticated user found");
      return;
    }

    const file = input.files[0];

    try {
      const uploadedFile = await this.fileApi.uploadFile(file, { folder: "users" });
      const imageUrl = uploadedFile.publicUrl;
      this.profileForm.patchValue({ imageUrl });
      await this.profileEffects.updateProfile(currentUser.id, { imageUrl });
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Failed to update image"), "error");
    } finally {
      input.value = "";
    }
  }

  async removeAvatar(): Promise<void> {
    const currentUser = this.authState.user();
    if (!currentUser?.imageUrl) {
      return;
    }

    try {
      await this.profileEffects.deleteImage();
      this.profileForm.patchValue({ imageUrl: null });
      this.toastEffects.show("Image removed", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Failed to remove image"), "error");
    }
  }

  private getProfilePermissions(): ProfilePermission[] {
    return this.profileState.profile()?.role?.permissions ?? [];
  }

  private matchesSearch(permission: ProfilePermission, term: string): boolean {
    return (
      permission.module.toLowerCase().includes(term) ||
      permission.action.toLowerCase().includes(term) ||
      (permission.description ?? "").toLowerCase().includes(term)
    );
  }
}

function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const current = control.get("newPassword")?.value;
  const confirm = control.get("confirmPassword")?.value;
  if (!current || !confirm) {
    return null;
  }
  return (() => {
    if (current === confirm) {
      return null;
    }
    return { passwordMismatch: true };
  })();
}
